import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import { SimpleInput } from "@/core/components/ui/simple-input";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import type { SpreadsheetColumnFilter } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-region-view";
import { SECTION_LABEL_CLASSNAME } from "./constants";

interface ValueFilterEditorProps {
  values: string[];
  filter: SpreadsheetColumnFilter;
  onChange: (filter: SpreadsheetColumnFilter) => void;
}

const VALUE_SEARCH_THRESHOLD = 8;

const getValueLabel = (value: string) => (value === "" ? "(Blanks)" : value);

export const ValueFilterEditor = ({
  values,
  filter,
  onChange,
}: ValueFilterEditorProps) => {
  const [search, setSearch] = useState("");
  const searchQuery = search.trim().toLocaleLowerCase();
  const shownValues =
    searchQuery === ""
      ? values
      : values.filter((value) =>
          getValueLabel(value).toLocaleLowerCase().includes(searchQuery),
        );
  const selectedCount = filter.selectedValues?.length ?? values.length;

  const toggleValue = (value: string, checked: boolean) => {
    const selected = new Set(filter.selectedValues ?? values);
    if (checked) selected.add(value);
    else selected.delete(value);
    onChange({ ...filter, selectedValues: Array.from(selected) });
  };

  return (
    <>
      <Stack direction="row" align="center" justify="between" fullWidth>
        <span className={SECTION_LABEL_CLASSNAME}>Filter by value</span>
        <Typography variant="bodyW3" textColor="secondary">
          {searchQuery === ""
            ? `${selectedCount} of ${values.length}`
            : `${shownValues.length} ${shownValues.length === 1 ? "match" : "matches"}`}
        </Typography>
      </Stack>

      {values.length > VALUE_SEARCH_THRESHOLD && (
        <div className="w-full">
          <SimpleInput
            aria-label="Search values"
            placeholder="Search values"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            startAdornment={<MagnifyingGlassIcon className="size-4" />}
            className="h-9 text-sm placeholder:text-sm md:text-sm"
          />
        </div>
      )}

      <Stack direction="row" align="center" gap="sm" fullWidth>
        <Button
          variant="text"
          size="xs"
          className="h-auto p-0"
          disabled={filter.selectedValues === null}
          onClick={() => onChange({ ...filter, selectedValues: null })}
        >
          Select all
        </Button>
        <span className="text-tertiary-text">·</span>
        <Button
          variant="text"
          size="xs"
          className="h-auto p-0"
          disabled={selectedCount === 0}
          onClick={() => onChange({ ...filter, selectedValues: [] })}
        >
          Deselect all
        </Button>
      </Stack>

      <div className="quiet-scrollbar min-h-[4.5rem] w-full flex-1 overflow-y-auto rounded-lg border border-stroke-tertiary p-1">
        {shownValues.length === 0 ? (
          <Typography
            variant="bodyW3"
            textColor="secondary"
            className="px-2 py-1.5"
          >
            No values match “{search}”.
          </Typography>
        ) : (
          shownValues.map((value) => (
            <label
              key={value || "__blank__"}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-surface-secondary"
            >
              <Checkbox
                checked={
                  filter.selectedValues === null ||
                  filter.selectedValues.includes(value)
                }
                onCheckedChange={(checked) =>
                  toggleValue(value, checked === true)
                }
              />
              <span
                className={cn("truncate", {
                  "text-secondary-text": value === "",
                })}
              >
                {getValueLabel(value)}
              </span>
            </label>
          ))
        )}
      </div>
    </>
  );
};
